<!-- Rating Summary -->
@if (ratingSummary()) {
  <div class="rating-summary">
    <div class="rating-overview">
      <div class="average-rating">
        <span class="rating-number">{{ ratingSummary()!.averageRating }}</span>
        <div class="stars" (click)="onStarsClick()" title="Click to write a review">
          @for (i of getStarArray(5); track i) {
            <span 
              class="star" 
              [class]="getStarClass(i, ratingSummary()!.averageRating)">
              ★
            </span>
          }
        </div>
        <span class="rating-count">({{ ratingSummary()!.totalRatings }} ratings)</span>
      </div>
      
      <div class="rating-breakdown">
        @for (i of [5,4,3,2,1]; track i; let idx = $index) {
          <div class="rating-bar" (click)="onRatingBarClick(i)" [attr.title]="'Click to filter ' + i + '-star reviews'">
            <span class="star-label">{{ i }}★</span>
            <div class="bar-container">
              <div class="bar-fill" 
                   [style.width.%]="getRatingPercentage([ratingSummary()!.fiveStarCount, ratingSummary()!.fourStarCount, ratingSummary()!.threeStarCount, ratingSummary()!.twoStarCount, ratingSummary()!.oneStarCount][idx])">
              </div>
            </div>
            <span class="count">{{ [ratingSummary()!.fiveStarCount, ratingSummary()!.fourStarCount, ratingSummary()!.threeStarCount, ratingSummary()!.twoStarCount, ratingSummary()!.oneStarCount][idx] }}</span>
          </div>
        }
      </div>
    </div>

    <!-- Create Rating Button -->
    @if (canRate() && !showRatingForm()) {
      <div class="rating-actions">
        <button class="btn btn-primary" (click)="showCreateRatingForm()">
          Write a Review
        </button>
      </div>
    }

    <!-- Rating Form -->
    @if (showRatingForm()) {
      <div class="rating-form">
        <h3>Write a Review</h3>
        
        <div class="form-group">
          <label>Overall Rating *</label>
          <div class="star-rating">
            @for (i of getStarArray(5); track i) {
              <span 
                class="star" 
                [class.star-filled]="i <= newRating.rating"
                [class.star-empty]="i > newRating.rating"
                (click)="setRating(i)">
                ★
              </span>
            }
          </div>
        </div>

        <div class="form-group">
          <label for="reviewTitle">Review Title</label>
          <input 
            type="text" 
            id="reviewTitle"
            [(ngModel)]="newRating.reviewTitle" 
            placeholder="Summarize your experience"
            class="form-control">
        </div>

        <div class="form-group">
          <label for="review">Your Review</label>
          <textarea 
            id="review"
            [(ngModel)]="newRating.review" 
            placeholder="Tell us about your experience with this product"
            class="form-control"
            rows="4">
          </textarea>
        </div>

        <!-- Detailed Ratings -->
        <div class="detailed-ratings">
          <h4>Rate Specific Aspects (Optional)</h4>
          
          <div class="rating-item">
            <label>Quality</label>
            <div class="star-rating">
              @for (i of getStarArray(5); track i) {
                <span 
                  class="star" 
                  [class.star-filled]="i <= (newRating.qualityRating || 0)"
                  [class.star-empty]="i > (newRating.qualityRating || 0)"
                  (click)="setDetailRating('quality', i)">
                  ★
                </span>
              }
            </div>
          </div>

          <div class="rating-item">
            <label>Value for Money</label>
            <div class="star-rating">
              @for (i of getStarArray(5); track i) {
                <span 
                  class="star" 
                  [class.star-filled]="i <= (newRating.valueRating || 0)"
                  [class.star-empty]="i > (newRating.valueRating || 0)"
                  (click)="setDetailRating('value', i)">
                  ★
                </span>
              }
            </div>
          </div>

          <div class="rating-item">
            <label>Shipping</label>
            <div class="star-rating">
              @for (i of getStarArray(5); track i) {
                <span 
                  class="star" 
                  [class.star-filled]="i <= (newRating.shippingRating || 0)"
                  [class.star-empty]="i > (newRating.shippingRating || 0)"
                  (click)="setDetailRating('shipping', i)">
                  ★
                </span>
              }
            </div>
          </div>

          <div class="rating-item">
            <label>Customer Service</label>
            <div class="star-rating">
              @for (i of getStarArray(5); track i) {
                <span 
                  class="star" 
                  [class.star-filled]="i <= (newRating.customerServiceRating || 0)"
                  [class.star-empty]="i > (newRating.customerServiceRating || 0)"
                  (click)="setDetailRating('customerService', i)">
                  ★
                </span>
              }
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button 
            class="btn btn-secondary" 
            (click)="hideCreateRatingForm()"
            [disabled]="isSubmittingRating()">
            Cancel
          </button>
          <button 
            class="btn btn-primary" 
            (click)="submitRating()"
            [disabled]="isSubmittingRating()">
            @if (!isSubmittingRating()) {
              <span>Submit Review</span>
            } @else {
              <span>Submitting...</span>
            }
          </button>
        </div>
      </div>
    }
  </div>
}

<!-- Loading State for Summary -->
@if (isLoadingSummary()) {
  <div class="loading-state">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading rating summary...</p>
  </div>
}

<!-- No Ratings Message -->
@if (!isLoadingSummary() && !ratingSummary()) {
  <div class="no-ratings">
    <p>No ratings yet. Be the first to review this product!</p>
    @if (canRate()) {
      <button class="btn btn-primary" (click)="showCreateRatingForm()">
        Write the First Review
      </button>
    }
  </div>
}

<!-- Customer Ratings List -->
@if (ratings().length > 0) {
  <div class="ratings-list">
    <h3>Customer Reviews</h3>
    
    @for (rating of ratings(); track rating.id) {
      <div class="rating-card">
        <div class="rating-header">
          <div class="customer-info">
            <div class="customer-avatar">{{ rating.customerInitials }}</div>
            <div class="customer-details">
              <div class="customer-name">{{ rating.customerDisplayName }}</div>
              <div class="rating-meta">
                <div class="stars">
                  @for (i of getStarArray(rating.rating); track i) {
                    <span class="star star-filled">★</span>
                  }
                </div>
                <span class="rating-date">{{ formatDate(rating.createdAt) }}</span>
                @if (rating.isVerifiedPurchase) {
                  <span class="verified-badge">✓ Verified Purchase</span>
                }
              </div>
            </div>
          </div>
        </div>

        @if (rating.reviewTitle || rating.review) {
          <div class="rating-content">
            @if (rating.reviewTitle) {
              <h4 class="review-title">{{ rating.reviewTitle }}</h4>
            }
            @if (rating.review) {
              <p class="review-text">{{ rating.review }}</p>
            }
          </div>
        }

        <!-- Detailed Ratings -->
        @if (rating.qualityRating || rating.valueRating || rating.shippingRating || rating.customerServiceRating) {
          <div class="detailed-rating-display">
            @if (rating.qualityRating) {
              <div class="detail-rating">
                <span class="detail-label">Quality:</span>
                <div class="stars">
                  @for (i of getStarArray(rating.qualityRating); track i) {
                    <span class="star star-filled">★</span>
                  }
                </div>
              </div>
            }
            
            @if (rating.valueRating) {
              <div class="detail-rating">
                <span class="detail-label">Value:</span>
                <div class="stars">
                  @for (i of getStarArray(rating.valueRating); track i) {
                    <span class="star star-filled">★</span>
                  }
                </div>
              </div>
            }
            
            @if (rating.shippingRating) {
              <div class="detail-rating">
                <span class="detail-label">Shipping:</span>
                <div class="stars">
                  @for (i of getStarArray(rating.shippingRating); track i) {
                    <span class="star star-filled">★</span>
                  }
                </div>
              </div>
            }
            
            @if (rating.customerServiceRating) {
              <div class="detail-rating">
                <span class="detail-label">Customer Service:</span>
                <div class="stars">
                  @for (i of getStarArray(rating.customerServiceRating); track i) {
                    <span class="star star-filled">★</span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- Loading State for Ratings -->
@if (isLoadingRatings()) {
  <div class="loading-state">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading reviews...</p>
  </div>
}
